import "@/App.css";
import React, { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Build from "@/pages/Build";
import Invest from "@/pages/Invest";
// Heavier, non-landing pages are lazy-loaded so they don't bloat the homepage bundle.
// Landing/Build/Invest stay eager (ad-funnel pages) — this preserves the iOS instant-paint fix.
const Admin = React.lazy(() => import("@/pages/Admin"));
const Live = React.lazy(() => import("@/pages/Live"));
const Legal = React.lazy(() => import("@/pages/Legal"));

function RouteFallback() {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 40, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 22, background: "#08080A",
        fontFamily: "Manrope, -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
      }}
    >
      <div style={{ color: "#EDEDED", fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}>Anubhav Sahni</div>
      <div style={{ width: 120, height: 2, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden" }}>
        <i style={{ display: "block", height: "100%", width: "40%", background: "#D9A441", borderRadius: 2, animation: "asSlide 1.1s ease-in-out infinite" }} />
      </div>
    </div>
  );
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("ROUTE_ERROR_BOUNDARY", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div data-testid="route-error" style={{ minHeight: "100vh", background: "#08080A", color: "#fff", padding: 40, fontFamily: "monospace" }}>
          <h2 style={{ color: "#D9A441" }}>Route error</h2>
          <pre style={{ whiteSpace: "pre-wrap" }}>{String(this.state.error?.message || this.state.error)}</pre>
          <pre style={{ whiteSpace: "pre-wrap", opacity: 0.6 }}>{String(this.state.error?.stack || "").slice(0, 800)}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function App() {
  const { ready } = useTranslation();
  useEffect(() => {
    if (ready && typeof window !== "undefined" && window.__hideSplash) {
      window.__hideSplash();
    }
  }, [ready]);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <RouteErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/admin" element={<Admin />} />
                <Route path="/build" element={<Build />} />
                <Route path="/invest" element={<Invest />} />
                <Route path="/invest/privacy" element={<Legal type="invest_privacy" />} />
                <Route path="/invest/terms" element={<Legal type="invest_terms" />} />
                <Route path="/live" element={<Live />} />
                <Route path="/privacy" element={<Legal type="privacy" />} />
                <Route path="/terms" element={<Legal type="terms" />} />
                <Route path="/cookies" element={<Legal type="cookies" />} />
                <Route path="/refund" element={<Legal type="refund" />} />
                <Route path="/:lang" element={<Landing />} />
                <Route path="/" element={<Landing />} />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-center" />
      </div>
    </HelmetProvider>
  );
}

export default App;
